import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { SocketContext } from '../contexts/SocketContext';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { Home, MessageSquare, PlusSquare, Bell, Mail, User } from 'lucide-react';

const MobileTabBar: React.FC = () => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount } = useNotifications();
  const socketContext = useContext(SocketContext);
  const socket = socketContext?.socket;

  const [unreadMessages, setUnreadMessages] = useState(0);
  const lastUnreadFetchRef = useRef(0);
  const THROTTLE_MS = 2000;

  const fetchUnreadMessages = useCallback(async () => {
    if (user?.id == null) {
      setUnreadMessages(0);
      return;
    }
    const now = Date.now();
    if (now - lastUnreadFetchRef.current < THROTTLE_MS) return;
    lastUnreadFetchRef.current = now;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(apiUrl('/messages/unread-count'), {
        headers,
        timeout: 8000,
        skipNetworkErrorToast: true
      } as any);
      setUnreadMessages(response.data.count ?? 0);
    } catch (error) {
      console.error('Error fetching unread count in MobileTabBar:', error);
      setUnreadMessages(0);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id == null) return;
    fetchUnreadMessages();
    const interval = setInterval(fetchUnreadMessages, 60000);
    return () => clearInterval(interval);
  }, [user?.id, fetchUnreadMessages]);

  useEffect(() => {
    if (!socket || user?.id == null) return;

    const handleNewMessage = () => fetchUnreadMessages();
    const handleMessageRead = () => fetchUnreadMessages();

    socket.on('new_message', handleNewMessage);
    socket.on('message_read', handleMessageRead);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('message_read', handleMessageRead);
    };
  }, [socket, user?.id, fetchUnreadMessages]);

  // تحديث عداد الرسائل بناء على أحداث مخصصة إن وجدت
  useEffect(() => {
    const handleUpdateUnreadCount = () => {
      fetchUnreadMessages();
    };
    window.addEventListener('updateUnreadMessagesCount', handleUpdateUnreadCount);
    return () => {
      window.removeEventListener('updateUnreadMessagesCount', handleUpdateUnreadCount);
    };
  }, [fetchUnreadMessages]);

  const isActive = (paths: string[]) => {
    return paths.includes(location.pathname);
  };

  const handleTabClick = (path: string) => {
    navigate(path);
  };

  return (
    <div className="mobile-tab-bar">
      <div 
        onClick={() => handleTabClick('/')} 
        className={`tab-bar-item ${isActive(['/']) ? 'active' : ''}`}
      >
        <Home />
        <span>{t('header.home', 'الرئيسية')}</span>
      </div>

      <div 
        onClick={() => handleTabClick('/messages')} 
        className={`tab-bar-item relative ${isActive(['/messages']) ? 'active' : ''}`}
      >
        <MessageSquare />
        {unreadMessages > 0 && (
          <span className="tab-badge">{unreadMessages}</span>
        )}
        <span>{t('header.messages', 'الرسائل')}</span>
      </div>

      <div 
        onClick={() => handleTabClick('/create-auction')} 
        className={`tab-bar-item ${isActive(['/create-auction']) ? 'active' : ''}`}
      >
        <PlusSquare />
        <span>{t('header.sell', 'أضف مزاد')}</span>
      </div>

      <div 
        onClick={() => handleTabClick('/notifications')} 
        className={`tab-bar-item relative ${isActive(['/notifications']) ? 'active' : ''}`}
      >
        <Bell />
        {unreadCount > 0 && (
          <span className="tab-badge">{unreadCount}</span>
        )}
        <span>{t('header.notifications', 'الإشعارات')}</span>
      </div>

      <div
        onClick={() => handleTabClick('/contact')}
        className={`tab-bar-item ${isActive(['/contact']) ? 'active' : ''}`}
      >
        <Mail />
        <span>{t('header.contact', 'تواصل معنا')}</span>
      </div>

      <div 
        onClick={() => handleTabClick('/profile')} 
        className={`tab-bar-item ${isActive(['/profile']) ? 'active' : ''}`}
      >
        <User />
        <span>{t('header.profile', 'حسابي')}</span>
      </div>
    </div>
  );
};

export default MobileTabBar;
